import React from 'react';
import Icon from './Icon';

// ─────────────────────────────────────────────────────────────────────────────
// Peças de UI reaproveitadas pelas telas.
//
// Equivalente ao components/v2/ui.js do Nexus, com as MESMAS classes e a mesma
// estrutura de marcação — é o que faz as duas aplicações parecerem a mesma
// aplicação. O que muda é só o conteúdo: aqui as pills falam de ticket e
// incidente, lá falam de solicitação e fase.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Cartão de número da linha de métricas (mesmo layout do Nexus).
 *
 * Com `aoClicar`, o cartão vira um seletor: ele passa a responder ao clique e
 * ao Enter/Espaço (é um `role="button"`, então o teclado precisa funcionar) e
 * `ativo` marca qual dos cartões está sendo mostrado na lista de baixo.
 *
 * `alerta` pinta o cartão no tom de atenção — para o que não pode passar
 * despercebido quando aparece.
 */
export function Metric({
  icon, valor, label, delta, direcao, sub, aoClicar, ativo = false, alerta = false,
}) {
  const clicavel = typeof aoClicar === 'function';

  return (
    <div
      className={[
        'metric',
        clicavel && 'metric-click',
        ativo && 'metric-ativo',
        alerta && 'metric-alerta',
      ].filter(Boolean).join(' ')}
      onClick={clicavel ? aoClicar : undefined}
      role={clicavel ? 'button' : undefined}
      tabIndex={clicavel ? 0 : undefined}
      aria-pressed={clicavel ? ativo : undefined}
      onKeyDown={clicavel ? (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        aoClicar();
      } : undefined}
    >
      <div className="metric-top">
        <div className="metric-ic"><Icon name={icon} size={17} /></div>
        <div className="metric-label">{label}</div>
      </div>
      <div className="metric-value">{valor}</div>
      <div className="metric-foot">
        {delta ? (
          <span className={`delta ${direcao}`}>
            <Icon
              name="chevron-right"
              size={11}
              style={{ transform: direcao === 'up' ? 'rotate(-90deg)' : 'rotate(90deg)' }}
            /> {delta}
          </span>
        ) : <span />}
        <span className="metric-sub">{sub}</span>
      </div>
    </div>
  );
}

/** Etiqueta curta. `classe` pinta conforme o significado. */
export function Pill({ children, classe = '', title }) {
  return <span className={`pill ${classe}`} title={title}>{children}</span>;
}

/**
 * Situação de um incidente.
 * As classes reaproveitam as cores de status do Nexus (--c-recebida,
 * --c-atend, --c-concluida), então os dois apps usam a mesma paleta para
 * "esperando", "em curso" e "encerrado".
 */
const STATUS_INCIDENTE = {
  pendente: { label: 'Pendente', classe: 'st-recebida-pill' },
  pendente_possivel_sistemas: { label: 'Possível p/ Sistemas', classe: 'st-recebida-pill' },
  em_analise: { label: 'Em análise', classe: 'st-em_atendimento-pill' },
  'em análise': { label: 'Em análise', classe: 'st-em_atendimento-pill' },
  em_atendimento: { label: 'Em atendimento', classe: 'st-em_atendimento-pill' },
  // Saiu do suporte: cor própria, como o indigo do 1.x, para não se confundir
  // nem com "esperando" nem com "concluído".
  setor_sistemas: { label: 'Setor de Sistemas', classe: 'st-sistemas-pill' },
  suporte_avancado: { label: 'Suporte avançado', classe: 'st-sistemas-pill' },
  concluido: { label: 'Concluído', classe: 'st-concluida-pill' },
  'concluído': { label: 'Concluído', classe: 'st-concluida-pill' },
  concluido_sistemas: { label: 'Concluído por Sistemas', classe: 'st-conclsis-pill' },
  resolvido: { label: 'Resolvido', classe: 'st-concluida-pill' },
  cancelado: { label: 'Cancelado', classe: 'st-devolvida-pill' },
  devolvido: { label: 'Devolvido', classe: 'st-devolvida-pill' },
};

/**
 * Cor do status, para pintar algo que não é a pill — a faixa do card, por
 * exemplo. Vem do MESMO mapa das pills, então card e etiqueta nunca discordam.
 */
const COR_DA_CLASSE = {
  'st-recebida-pill': 'var(--c-recebida)',
  'st-em_atendimento-pill': 'var(--c-atend)',
  'st-concluida-pill': 'var(--c-concluida)',
  'st-devolvida-pill': 'var(--c-devolvida)',
  'st-sistemas-pill': 'var(--c-cus)',
  'st-conclsis-pill': 'var(--c-alt)',
  'st-pendente-pill': 'var(--warn)',
};

export function corDoStatusIncidente(status) {
  const s = STATUS_INCIDENTE[String(status || '').toLowerCase()];
  return (s && COR_DA_CLASSE[s.classe]) || 'var(--line2)';
}

export function corDoStatusTi(status) {
  const s = STATUS_TI[String(status || '').toLowerCase()];
  return (s && COR_DA_CLASSE[s.classe]) || 'var(--line2)';
}

/**
 * Status que não está no mapa vira texto legível em vez de aparecer cru.
 * O banco guarda em snake_case ("pendente_possivel_sistemas") e um valor novo
 * cadastrado lá não pode chegar assim na tela.
 */
function rotuloDeStatus(status) {
  const bruto = String(status || '').trim();
  if (!bruto) return '—';
  const texto = bruto.replace(/_/g, ' ').toLowerCase();
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

export function StatusIncidentePill({ status }) {
  const s = STATUS_INCIDENTE[String(status || '').toLowerCase()];
  if (s) return <span className={`pill ${s.classe}`}>{s.label}</span>;
  return <span className="pill" title={status}>{rotuloDeStatus(status)}</span>;
}

/**
 * Situação de um chamado de T.I.
 *
 * Os rótulos são os que o banco grava ("Aberto", "Em Atendimento", "Solução
 * Pendente", "Concluído", "Cancelado"). "Solução Pendente" fica em vermelho,
 * como no 1.x: é o chamado que travou esperando alguém.
 */
const STATUS_TI = {
  aberto: { label: 'Aberto', classe: 'st-recebida-pill' },
  'em atendimento': { label: 'Em Atendimento', classe: 'st-em_atendimento-pill' },
  'em andamento': { label: 'Em andamento', classe: 'st-em_atendimento-pill' },
  'solução pendente': { label: 'Solução Pendente', classe: 'st-pendente-pill' },
  'solucao pendente': { label: 'Solução Pendente', classe: 'st-pendente-pill' },
  fechado: { label: 'Fechado', classe: 'st-concluida-pill' },
  concluido: { label: 'Concluído', classe: 'st-concluida-pill' },
  'concluído': { label: 'Concluído', classe: 'st-concluida-pill' },
  cancelado: { label: 'Cancelado', classe: 'st-devolvida-pill' },
};

export function StatusTiPill({ status }) {
  const s = STATUS_TI[String(status || '').toLowerCase()]
    || { label: status || '—', classe: '' };
  return <span className={`pill ${s.classe}`}>{s.label}</span>;
}

/** Iniciais em círculo colorido. */
export function Avatar({ iniciais, cor, size = 22, className = '' }) {
  return (
    <span
      className={`kc-avatar ${className}`}
      style={{ background: cor || 'var(--team)', width: size, height: size, fontSize: Math.round(size * 0.42) }}
    >
      {iniciais}
    </span>
  );
}

/** Nada para mostrar. */
export function EmptyState({ children }) {
  return <div className="empty-state">{children}</div>;
}

/** Faixa de contexto no topo de uma tela — explica o que ela faz. */
export function FlowHint({ icon, children }) {
  return (
    <div className="flow-hint">
      {icon && <Icon name={icon} size={15} style={{ marginRight: 6, color: 'var(--team)' }} />}
      {children}
    </div>
  );
}

/** Cabeçalho de painel: título, contagem discreta e ações à direita. */
export function PanelHead({ titulo, sub, children }) {
  return (
    <div className="panel-head">
      <div className="panel-title">
        {titulo}{sub ? <span className="sub">{sub}</span> : null}
      </div>
      {children}
    </div>
  );
}

/** Rótulo + valor, no formato das fichas do Nexus. */
export function Campo({ label, children }) {
  return (
    <div className="fld">
      <span className="fld-label">{label}</span>
      <span className="fld-value">{children}</span>
    </div>
  );
}

/** Número de ticket clicável (abre as interações). */
export function TicketTag({ numero, onClick, title = 'Ver interações do ticket' }) {
  if (!numero) return <span className="fld-value">—</span>;
  if (!onClick) return <span className="ticket-tag">#{numero}</span>;
  return (
    <button type="button" className="ticket-tag" onClick={onClick} title={title}>
      <Icon name="ticket" size={11} /> {numero}
    </button>
  );
}
